import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiMaximize2, FiX } from 'react-icons/fi';
import { useAuth } from '@/hooks/useAuth';
import {
  useCompleteFollowUpMutation,
  useGetFollowUpsQuery,
} from '@/store/api/apiSlice';
import { storage } from '@/utils/storage';
import {
  countOverdueScope,
  getTodayFollowUps,
  todayCalendarKey,
} from './followUpUtils';
import FollowUpRow from './FollowUpRow';
import './followups-panel.css';

/** Blocking daily reminder when the user has follow-ups due today or overdue. */
export default function TodayFollowUpReminder() {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id;
  const { data: items = [], isLoading, isFetching, isSuccess } = useGetFollowUpsQuery(undefined, {
    skip: !isAuthenticated,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [completeFollowUp] = useCompleteFollowUpMutation();
  const [dismissed, setDismissed] = useState(false);

  const todayItems = useMemo(() => getTodayFollowUps(items), [items]);
  const overdueCount = useMemo(() => countOverdueScope(items), [items]);
  const todayKey = todayCalendarKey();
  const alreadyDismissedToday = userId
    ? storage.getFollowUpReminderDismissedDate(userId) === todayKey
    : false;

  const shouldShow = Boolean(
    isAuthenticated &&
      userId &&
      isSuccess &&
      !isLoading &&
      !dismissed &&
      !alreadyDismissedToday &&
      todayItems.length > 0,
  );

  useEffect(() => {
    if (!userId) return;
    setDismissed(storage.getFollowUpReminderDismissedDate(userId) === todayKey);
  }, [userId, todayKey]);

  useEffect(() => {
    if (!shouldShow) return;
    const blockEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.preventDefault();
    };
    document.addEventListener('keydown', blockEscape, true);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', blockEscape, true);
      document.body.style.overflow = 'unset';
    };
  }, [shouldShow]);

  const handleDismiss = () => {
    if (userId) storage.setFollowUpReminderDismissedDate(userId, todayKey);
    setDismissed(true);
  };

  const handleOpenChecklist = () => {
    handleDismiss();
    window.dispatchEvent(new CustomEvent('cotaskr:open-follow-ups'));
  };

  const handleComplete = async (id: string) => {
    await completeFollowUp(id).unwrap().catch(() => null);
  };

  if (!shouldShow) return null;

  const visibleItems = todayItems.slice(0, 5);
  const hiddenCount = todayItems.length - visibleItems.length;

  return createPortal(
    <div
      className="fixed inset-0 z-[25000] flex items-center justify-center bg-[#f1efe9]/90 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="follow-up-reminder-title"
    >
      <div className="fu-panel w-full max-w-[420px] animate-fade-up" style={{ maxHeight: 'min(520px, calc(100vh - 48px))' }}>
        <div className="fu-head">
          <h2 id="follow-up-reminder-title" className="fu-title">
            {t('todayPanel.fullscreenTitle')}
          </h2>
          <div className="fu-acts">
            <button type="button" onClick={handleOpenChecklist} title={t('todayPanel.reminderOpenChecklist')} aria-label={t('todayPanel.reminderOpenChecklist')}>
              <FiMaximize2 />
            </button>
            <button type="button" onClick={handleDismiss} aria-label={t('common.close')}>
              <FiX />
            </button>
          </div>
        </div>

        <div className="fu-tabs">
          <button type="button" className="fu-tab on">
            {t('todayPanel.tabToday')} <span className="fu-n">{todayItems.length}</span>
          </button>
        </div>

        {overdueCount > 0 && (
          <p className="px-4 pb-2 text-[12px] text-[#a32d2d]">
            {t('todayPanel.reminderOverdueNote', { count: overdueCount })}
          </p>
        )}

        <div className="fu-list">
          {isFetching && todayItems.length === 0 ? (
            <div className="fu-empty">{t('common.loading')}</div>
          ) : (
            visibleItems.map((item) => (
              <FollowUpRow
                key={item.id}
                item={item}
                onComplete={handleComplete}
                onPostpone={() => {}}
                onDelete={() => {}}
                showActions={false}
              />
            ))
          )}
          {hiddenCount > 0 && (
            <div className="fu-empty" style={{ padding: '16px' }}>
              {t('todayPanel.reminderMore', { count: hiddenCount })}
            </div>
          )}
        </div>

        <div className="flex gap-2 border-t border-[rgba(28,28,26,0.1)] p-3">
          <button
            type="button"
            onClick={handleOpenChecklist}
            className="fu-addbtn flex-1 justify-center"
            style={{ background: '#fff', color: '#6b6b64', border: '0.5px solid rgba(28,28,26,0.1)' }}
          >
            {t('todayPanel.reminderOpenChecklist')}
          </button>
          <button type="button" onClick={handleDismiss} className="fu-addbtn flex-1 justify-center">
            {t('todayPanel.reminderDismiss')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
